import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { enfermeriaApi, IndicacionCreatePayload } from '../lib/api'
import { useToast } from '../components/Toast'

interface Dieta {
  id: number
  codigo: string
  nombre: string
}

interface Tiempo {
  id: number
  codigo: string
  nombre: string
  orden: number
  es_refrigerio: boolean
}

// 24 habitaciones CM (sede_id=1)
const HABITACIONES = Array.from({ length: 24 }, (_, i) =>
  String(i + 1).padStart(3, '0'),
)

const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
]

function diaSemanaHoy(): number {
  const js = new Date().getDay()
  return js === 0 ? 7 : js
}

// cellKey "dieta_codigo|tiempo_codigo"
const cellKey = (d: string, t: string) => `${d}|${t}`

interface Resultado {
  cellId: string
  ok: boolean
  msg: string
}

const LABEL = 'block text-[11px] uppercase tracking-[0.12em] text-[#8B97C8] font-semibold mb-1'
const INPUT =
  'w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary'

export default function EnfermeriaIndicacionMatriz() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()

  // Catálogos
  const [dietas, setDietas] = useState<Dieta[]>([])
  const [tiempos, setTiempos] = useState<Tiempo[]>([])
  const [catalogosListos, setCatalogosListos] = useState(false)

  // Form general (compartido por todas las celdas marcadas)
  const [paciente_nombre, setPacienteNombre] = useState('')
  const [paciente_documento, setPacienteDocumento] = useState('')
  const [habitacion_codigo, setHabitacion] = useState('001')
  const [dia_semana, setDiaSemana] = useState(diaSemanaHoy())
  const [indicacion_dr, setIndicacionDr] = useState('')
  const [notas, setNotas] = useState('')

  // Matriz: Set de cellKeys marcadas
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set())

  // Cobertura del menú vigente para el día elegido: solo las celdas con
  // platos se pueden marcar. null = aún no cargada.
  const [cobertura, setCobertura] = useState<Set<string> | null>(null)

  // UI
  const [submitting, setSubmitting] = useState(false)
  const [resultados, setResultados] = useState<Resultado[]>([])

  useEffect(() => {
    let cancelado = false
    Promise.all([enfermeriaApi.dietas(), enfermeriaApi.tiempos()])
      .then(([ds, ts]) => {
        if (cancelado) return
        setDietas(ds.filter((d: any) => d.activo !== false))
        setTiempos(ts.sort((a: Tiempo, b: Tiempo) => a.orden - b.orden))
        setCatalogosListos(true)
      })
      .catch((err) => {
        showToast(`No se cargaron catálogos: ${err.message}`, 'error')
      })
    return () => {
      cancelado = true
    }
  }, [showToast])

  // Cobertura por día: al cambiar el día se recarga y se desmarcan las
  // celdas que ese día no tienen menú (evita 422 del backend).
  useEffect(() => {
    let cancelado = false
    setCobertura(null)
    enfermeriaApi
      .cobertura(dia_semana)
      .then((res) => {
        if (cancelado) return
        const disponibles = new Set(
          res.celdas.map((c) => cellKey(c.dieta_codigo, c.tiempo_codigo)),
        )
        setCobertura(disponibles)
        setMarcadas((prev) => {
          const next = new Set([...prev].filter((k) => disponibles.has(k)))
          return next.size === prev.size ? prev : next
        })
      })
      .catch(() => {
        if (!cancelado) setCobertura(null)
      })
    return () => {
      cancelado = true
    }
  }, [dia_semana])

  const disponible = (d_codigo: string, t_codigo: string) =>
    cobertura === null || cobertura.has(cellKey(d_codigo, t_codigo))

  // Toggle de una celda. Regla: 1 dieta por tiempo (columna).
  function toggleCelda(d_codigo: string, t_codigo: string) {
    if (!disponible(d_codigo, t_codigo)) return
    const key = cellKey(d_codigo, t_codigo)
    setMarcadas((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        for (const k of next) {
          if (k.endsWith(`|${t_codigo}`)) next.delete(k)
        }
        next.add(key)
      }
      return next
    })
  }

  // Marcar fila completa: una dieta en todos los tiempos CON menú.
  function marcarFila(d_codigo: string) {
    setMarcadas((prev) => {
      const next = new Set(prev)
      for (const t of tiempos) {
        if (!disponible(d_codigo, t.codigo)) continue
        for (const k of next) {
          if (k.endsWith(`|${t.codigo}`)) next.delete(k)
        }
        next.add(cellKey(d_codigo, t.codigo))
      }
      return next
    })
  }

  function limpiarMatriz() {
    setMarcadas(new Set())
    setResultados([])
  }

  const formularioCompleto = useMemo(
    () =>
      paciente_nombre.trim().length >= 3 &&
      habitacion_codigo &&
      indicacion_dr.trim().length >= 2 &&
      marcadas.size > 0,
    [paciente_nombre, habitacion_codigo, indicacion_dr, marcadas],
  )

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formularioCompleto || submitting) return

    setSubmitting(true)
    setResultados([])

    const baseBody = {
      habitacion_codigo,
      paciente_nombre: paciente_nombre.trim(),
      paciente_documento: paciente_documento.trim() || null,
      dia_semana,
      indicacion_dr: indicacion_dr.trim(),
      notas: notas.trim() || null,
    }

    // BUG-4 (QA 4-jul): SECUENCIAL, no Promise.all. En paralelo, las 3
    // peticiones corren antes de que exista el cliente y el upsert del
    // backend inserta un paciente duplicado por cada una (race condition
    // documentada en flujo_dietas). En serie, la 1a crea al paciente y
    // las siguientes lo reutilizan por (sede + habitación + nombre).
    const results: Resultado[] = []
    for (const key of Array.from(marcadas)) {
      const [dieta_codigo, tiempo_codigo] = key.split('|')
      const payload: IndicacionCreatePayload = {
        ...baseBody,
        dieta_codigo,
        tiempo_codigo,
      }
      try {
        const res = await enfermeriaApi.crearIndicacion(payload)
        results.push({
          cellId: key,
          ok: true,
          msg: `#${res.indicacion_id} (${dieta_codigo} · ${tiempo_codigo})`,
        })
      } catch (err: any) {
        const detail = err.response?.data?.detail || err.message
        const msg = typeof detail === 'string' ? detail : 'Error'
        results.push({
          cellId: key,
          ok: false,
          msg: `${dieta_codigo} · ${tiempo_codigo}: ${msg}`,
        })
      }
    }
    setResultados(results)
    setSubmitting(false)

    const oks = results.filter((r) => r.ok).length
    const fails = results.length - oks
    if (fails === 0) {
      showToast(`${oks} indicaciones creadas para ${baseBody.paciente_nombre}`, 'success')
      setPacienteNombre('')
      setPacienteDocumento('')
      setNotas('')
      setMarcadas(new Set())
    } else {
      showToast(`${oks} OK · ${fails} con error. Revisar abajo.`, 'error')
    }
  }

  return (
    <div className="min-h-screen">
      {/* Header degradado con logo blanco */}
      <header
        className="sticky top-0 z-30 px-6 py-3 flex items-center gap-4"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 33 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Matriz multi-dieta
          </h1>
          <p className="text-xs text-[#94A0CD]">
            Enfermería · dietas distintas por tiempo de comida — {user?.nombre_completo}
          </p>
        </div>
        <Link to="/enfermeria" className="text-sm text-[#7FD4FF] underline shrink-0">
          Modo simple
        </Link>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>

      <main className="p-4 md:p-6 space-y-6 mx-auto" style={{ maxWidth: 1400 }}>
        {!catalogosListos && <p className="text-gray-500">Cargando catálogos...</p>}

        {catalogosListos && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Formulario superior */}
            <div className="bg-white rounded-lg shadow p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Paciente (nombre completo)</label>
                  <input type="text" value={paciente_nombre} required minLength={3} maxLength={200}
                    placeholder="Ej. Pedro Méndez Romero" className={INPUT}
                    onChange={(e) => setPacienteNombre(e.target.value)} />
                </div>
                <div>
                  <label className={LABEL}>N° expediente / DUI (opcional)</label>
                  <input type="text" value={paciente_documento} maxLength={50}
                    placeholder="Evita duplicados al re-internar" className={INPUT}
                    onChange={(e) => setPacienteDocumento(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className={LABEL}>Habitación</label>
                  <select value={habitacion_codigo} className={INPUT}
                    onChange={(e) => setHabitacion(e.target.value)}>
                    {HABITACIONES.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Día semana</label>
                  <select value={dia_semana} className={INPUT}
                    onChange={(e) => setDiaSemana(Number(e.target.value))}>
                    {DIAS_SEMANA.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Médico que indica</label>
                  <input type="text" value={indicacion_dr} required minLength={2} maxLength={200}
                    placeholder="Ej. Dr. Ramírez" className={INPUT}
                    onChange={(e) => setIndicacionDr(e.target.value)} />
                </div>
              </div>
              <div className="mt-4">
                <label className={LABEL}>Notas clínicas (opcional)</label>
                <textarea value={notas} maxLength={2000} rows={2} className={INPUT}
                  onChange={(e) => setNotas(e.target.value)} />
              </div>
            </div>

            {/* Matriz */}
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                  Asignación dieta × tiempo
                </h3>
                <div
                  className="text-sm px-3 py-1 rounded-full border"
                  style={{
                    fontFamily: 'JetBrains Mono',
                    color: '#7FD4FF',
                    borderColor: 'rgba(0,110,180,.5)',
                    background: 'rgba(0,110,180,.14)',
                  }}
                >
                  {marcadas.size} {marcadas.size === 1 ? 'indicación marcada' : 'indicaciones marcadas'}
                </div>
              </div>

              <div className="overflow-x-auto border rounded" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th className="text-left p-3 sticky left-0 top-0 z-20"
                        style={{ background: '#23233f' }}>Dieta</th>
                      {tiempos.map((t) => (
                        <th key={t.codigo} className="p-2 text-center sticky top-0 z-10"
                          style={{ background: '#23233f', borderLeft: '1px solid rgba(140,170,230,.14)' }}>
                          <div className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t.codigo}</div>
                          <div className="text-[10px] font-normal text-[#7F8BBB]">{t.nombre}</div>
                        </th>
                      ))}
                      <th className="p-2 sticky top-0 z-10" style={{ background: '#23233f' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dietas.map((d, i) => (
                      <tr key={d.codigo}
                        className="transition-colors hover:!bg-[rgba(0,110,180,.10)] group"
                        style={{ background: i % 2 ? 'rgba(255,255,255,.03)' : 'transparent' }}>
                        <td className="p-3 sticky left-0 z-10"
                          style={{ background: '#23233f', borderTop: '1px solid rgba(140,170,230,.12)' }}>
                          <div className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{d.codigo}</div>
                          <div className="text-xs text-[#7F8BBB] truncate max-w-[180px]">{d.nombre}</div>
                        </td>
                        {tiempos.map((t) => {
                          const key = cellKey(d.codigo, t.codigo)
                          const checked = marcadas.has(key)
                          const habilitada = disponible(d.codigo, t.codigo)
                          return (
                            <td key={t.codigo} className="p-2 text-center"
                              style={{
                                borderTop: '1px solid rgba(140,170,230,.12)',
                                borderLeft: '1px solid rgba(140,170,230,.10)',
                              }}
                              title={habilitada ? undefined : 'No disponible para el día seleccionado'}>
                              <button
                                type="button"
                                aria-label={`${d.codigo} ${t.codigo}`}
                                disabled={!habilitada}
                                onClick={() => toggleCelda(d.codigo, t.codigo)}
                                className="inline-flex items-center justify-center transition-all"
                                style={{
                                  width: 44, height: 44, borderRadius: 12,
                                  cursor: habilitada ? 'pointer' : 'not-allowed',
                                  border: checked
                                    ? '1px solid rgba(127,212,255,.7)'
                                    : habilitada
                                      ? '1.5px solid rgba(140,170,230,.35)'
                                      : '1px solid rgba(140,170,230,.12)',
                                  background: checked
                                    ? 'linear-gradient(135deg,#006EB4,#143C78)'
                                    : habilitada
                                      ? 'rgba(12,14,32,.45)'
                                      : 'repeating-linear-gradient(45deg, rgba(255,255,255,.03) 0 6px, transparent 6px 12px)',
                                  boxShadow: checked ? '0 8px 18px -6px rgba(0,110,180,.7)' : 'none',
                                  color: '#fff', fontSize: 18, fontWeight: 700,
                                }}
                              >
                                {checked ? '✓' : habilitada ? '' : <span style={{ color: '#5A6491', fontSize: 13 }}>🔒</span>}
                              </button>
                            </td>
                          )
                        })}
                        <td className="p-2 text-center" style={{ borderTop: '1px solid rgba(140,170,230,.12)' }}>
                          <button type="button" onClick={() => marcarFila(d.codigo)}
                            title="Marcar esta dieta en todos los tiempos con menú"
                            className="text-xs px-2 py-1 rounded-full border whitespace-nowrap"
                            style={{ color: '#7FD4FF', borderColor: 'rgba(0,110,180,.5)', background: 'rgba(0,110,180,.10)' }}>
                            ✓ Marcar fila
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-2 text-[#7F8BBB]">
                Una columna (tiempo) admite solo 1 dieta: al marcar otra dieta en el mismo tiempo, la anterior se desmarca.
                Las casillas con 🔒 no tienen menú desarrollado para el día elegido (refrigerios: solo Trasplantados, Diabética y Libre de gluten los incluyen).
              </p>
            </div>

            {/* Acciones */}
            <div className="flex gap-3">
              <button type="submit" disabled={!formularioCompleto || submitting}
                className="flex-1 py-3 rounded font-semibold text-white relative overflow-hidden"
                style={{
                  minHeight: 48, borderRadius: 16,
                  background: formularioCompleto
                    ? 'linear-gradient(120deg,#006EB4,#143C78)'
                    : 'rgba(255,255,255,.06)',
                  color: formularioCompleto ? '#fff' : '#7F8BBB',
                  boxShadow: formularioCompleto ? '0 14px 30px -10px rgba(0,110,180,.7)' : 'none',
                  cursor: formularioCompleto && !submitting ? 'pointer' : 'not-allowed',
                }}>
                {formularioCompleto && (
                  <span aria-hidden style={{
                    position: 'absolute', top: 0, bottom: 0, width: '40%',
                    background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent)',
                    animation: 'avSheen 3.2s ease-in-out infinite',
                  }} />
                )}
                {submitting
                  ? 'Creando...'
                  : `Crear ${marcadas.size} ${marcadas.size === 1 ? 'indicación' : 'indicaciones'}`}
              </button>
              <button type="button" onClick={limpiarMatriz} disabled={submitting}
                className="px-6 py-3 font-semibold disabled:opacity-50"
                style={{
                  minHeight: 48, borderRadius: 16, color: '#E6EAF7',
                  background: 'rgba(255,255,255,.04)', border: '1.5px solid rgba(140,170,230,.3)',
                }}>
                Limpiar
              </button>
            </div>
          </form>
        )}

        {/* Resultados del último submit */}
        {resultados.length > 0 && (
          <section className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Resultados</h3>
            <ul className="space-y-1 text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
              {resultados.map((r) => (
                <li key={r.cellId} className={r.ok ? 'text-green-700' : 'text-red-700'}>
                  {r.ok ? '✓' : '✗'} {r.msg}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  )
}
